import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  GitHub as GitHubIcon,
  Language as WebsiteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import StarRating from './StarRating';
import ReviewCard from './ReviewCard';
import { submitReview, SubmitReviewData, Review } from '../api/reviewsApi';
import useAuthStore from '../../../store/authStore';
import { brand } from '../../../config/brand';

const MAX_CHARS = 1000;

const ReviewSubmitForm: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();

  const [rating, setRating] = useState<number>(0);
  const [text, setText] = useState('');
  const [socialLinks, setSocialLinks] = useState({
    linkedin: '',
    twitter: '',
    facebook: '',
    instagram: '',
    github: '',
    website: '',
  });
  const [socialEmbedUrl, setSocialEmbedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSocialChange = (field: string, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (text.trim().length < 10) {
      setError('Please write at least 10 characters for your review.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const filteredLinks = Object.fromEntries(
        Object.entries(socialLinks).filter(([, v]) => v.trim() !== '')
      );

      const data: SubmitReviewData = {
        rating,
        text: text.trim(),
        ...(Object.keys(filteredLinks).length > 0 && {
          socialLinks: filteredLinks,
        }),
        ...(socialEmbedUrl.trim() && {
          socialEmbedUrl: socialEmbedUrl.trim(),
        }),
        // Attach user info from auth store
        ...(user?.firstName && {
          userName: `${user.firstName} ${user.lastName || ''}`.trim(),
        }),
        ...(user?.profilePictureUrl && {
          userAvatarUrl: user.profilePictureUrl,
        }),
      };

      await submitReview(data);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to submit review. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Preview data
  const displayName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : 'You';
  const previewReview: Review = {
    _id: 'preview',
    userId: 'preview',
    userName: displayName,
    rating: rating || 5,
    text: text || 'Your review will appear here...',
    socialLinks:
      Object.values(socialLinks).some((v) => v.trim())
        ? socialLinks
        : undefined,
    status: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const socialFields = [
    { key: 'linkedin', label: 'LinkedIn URL', icon: <LinkedInIcon /> },
    { key: 'twitter', label: 'Twitter / X URL', icon: <TwitterIcon /> },
    { key: 'facebook', label: 'Facebook URL', icon: <FacebookIcon /> },
    { key: 'instagram', label: 'Instagram URL', icon: <InstagramIcon /> },
    { key: 'github', label: 'GitHub URL', icon: <GitHubIcon /> },
    { key: 'website', label: 'Website URL', icon: <WebsiteIcon /> },
  ];

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Card
          sx={{
            p: 5,
            textAlign: 'center',
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                : undefined,
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)',
            borderRadius: 3,
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 80, color: brand.colors.fire, mb: 2 }}
          />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your review has been submitted and is pending approval. Once
            approved, it will appear on our homepage.
          </Typography>
          <Button
            variant="contained"
            href="/"
            sx={{
              mt: 2,
              background: brand.gradients.button,
              color: brand.buttonTextColor,
              '&:hover': {
                background: brand.gradients.buttonHover,
              },
            }}
          >
            Back to Home
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Share Your Experience
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Help others discover {brand.productName}. Your feedback means the world
          to us.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Form */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                  : undefined,
              backdropFilter: 'blur(20px)',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.06)',
              borderRadius: 3,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Star Rating */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Your Rating *
              </Typography>
              <StarRating
                value={rating}
                onChange={(val) => setRating(val || 0)}
                size="large"
                precision={1}
              />
            </Box>

            {/* Review Text */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Your Review *
              </Typography>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder={`Tell us about your experience with ${brand.productName}...`}
                value={text}
                onChange={(e) =>
                  e.target.value.length <= MAX_CHARS &&
                  setText(e.target.value)
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <Typography
                variant="caption"
                color={
                  text.length > MAX_CHARS * 0.9
                    ? 'error.main'
                    : 'text.secondary'
                }
                sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
              >
                {text.length}/{MAX_CHARS}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Social Links */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Social Media Links
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Optional. Add your profiles to display alongside your review.
              </Typography>

              <Grid container spacing={2}>
                {socialFields.map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={field.label}
                      value={
                        socialLinks[
                          field.key as keyof typeof socialLinks
                        ]
                      }
                      onChange={(e) =>
                        handleSocialChange(field.key, e.target.value)
                      }
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              mr: 1,
                              color: 'text.secondary',
                              display: 'flex',
                            }}
                          >
                            {field.icon}
                          </Box>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Social Embed URL */}
            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Social Embed URL (optional)"
                placeholder={`e.g. a tweet or LinkedIn post about ${brand.productName}`}
                value={socialEmbedUrl}
                onChange={(e) => setSocialEmbedUrl(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
            </Box>

            {/* Submit */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={loading || rating === 0 || text.trim().length < 10}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.05rem',
                borderRadius: 2,
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': {
                  background: brand.gradients.buttonHover,
                },
                '&.Mui-disabled': {
                  background: 'action.disabledBackground',
                },
              }}
            >
              {loading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 100 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ mb: 2 }}
            >
              Preview
            </Typography>
            <ReviewCard review={previewReview} />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReviewSubmitForm;
